import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../contexts/account/AccountContexts';
import { getAccount } from '../../services/account/AccountService';
import { useAuth } from '../../contexts/auth/AuthContext';
import { getProjects } from '../../services/projects/ProjectService';

// Odstępy kolejnych prób po nieudanym pobraniu konta/projektów. Typowa przyczyna to zakładka wracająca
// po uśpieniu maszyny, gdy sieć jeszcze nie wstała — pierwsze ponowienie zwykle wystarcza.
const RETRY_DELAYS_MS = [1000, 3000, 6000, 12000];

// Jeden cykl pobierania naraz, współdzielony przez wszystkie instancje hooka — dlatego zakres
// modułu, nie useRef (hook montują równolegle Wrapper i RedirectToFirstProject, a StrictMode
// dokłada drugi run efektu); bez tego dublował się m.in. ciężki request /projects/?force=true.
// Promise (a nie boolean), żeby pominięta instancja mogła się zapisać na zakończenie cyklu.
let fetchInFlight: Promise<void> | null = null;

const useAccountData = (projectId?: string) => {
  const { i18n } = useTranslation();
  const { accountResponse, setAccountResponse, projectResponse, setProjectResponse } = useAccount();
  const { isAuthenticated, logout } = useAuth(); // Dodaj logout
  const [loading, setLoading] = useState(true);
  // Zmiana wartości wymusza ponowienie efektu pobierającego — jego pozostałe zależności są stabilne,
  // więc bez tego jedna nieudana próba zostawiałaby kontekst pusty aż do przeładowania strony.
  const [retryTick, setRetryTick] = useState(0);
  const retriesLeft = useRef(RETRY_DELAYS_MS.length);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    if (accountResponse && projectResponse) {
      setLoading(false);
      return;
    }

    if (fetchInFlight) {
      // Cudzy cykl w toku — po jego zakończeniu ponawiamy własny efekt: kontekst pełny → early
      // return zgasi loading; pusty (błąd / odmontowany właściciel) → ta instancja przejmuje fetch.
      let cancelled = false;
      void fetchInFlight.finally(() => {
        if (!cancelled) {
          setRetryTick((tick) => tick + 1);
        }
      });
      return () => {
        cancelled = true;
      };
    }

    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const fetchData = async () => {
      try {
        const accountData = accountResponse || (await getAccount());
        const projectData = projectResponse || (await getProjects());

        if (!accountData) {
          logout();
          setLoading(false);
          return;
        }

        if (!accountResponse) {
          await i18n.changeLanguage(accountData.language);
          setAccountResponse(accountData);
        }

        if (!projectResponse && projectData) {
          const force =
            !isNaN(Number(projectId)) &&
            accountData.isLimitedSuperAdmin &&
            !projectData.find((project) => project.id === Number(projectId));

          if (!force) {
            setProjectResponse(projectData);
          } else {
            const forcedProjectData = await getProjects(true);
            setProjectResponse(forcedProjectData);
          }
        }
        retriesLeft.current = RETRY_DELAYS_MS.length;
      } catch (error) {
        console.error('Error fetching account or project data:', error);

        const status = (error as any)?.response?.status;
        if (status === 401 || status === 403) {
          logout();
        } else if (retriesLeft.current > 0) {
          const delay = RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - retriesLeft.current];
          retriesLeft.current -= 1;
          // Bez setLoading(false) — trwa Loader, a nie pusty widok zbudowany na null-ach.
          retryTimer = setTimeout(() => setRetryTick((tick) => tick + 1), delay);
          return;
        }
      }

      setLoading(false);
    };

    // Kolejność ma znaczenie: zdjęcie flagi jest pierwszym ogniwem łańcucha, więc zapisani
    // obserwatorzy budzą się już po jej wyzerowaniu i mogą od razu przejąć fetch.
    fetchInFlight = fetchData().finally(() => {
      fetchInFlight = null;
    });

    return () => clearTimeout(retryTimer);
  }, [
    isAuthenticated,
    accountResponse,
    projectResponse,
    projectId,
    logout,
    i18n,
    setAccountResponse,
    setProjectResponse,
    retryTick,
  ]);

  // Po wyczerpaniu prób ponawiamy, gdy wróci sieć albo gdy użytkownik wróci do zakładki — inaczej
  // widok zostaje pusty aż do F5.
  useEffect(() => {
    if (!isAuthenticated || (accountResponse && projectResponse)) {
      return;
    }

    const resume = () => {
      // `loading` odróżnia próbę w toku od serii, która się już poddała — tylko tę drugą wznawiamy.
      if (loading || document.visibilityState !== 'visible') {
        return;
      }

      retriesLeft.current = RETRY_DELAYS_MS.length;
      setLoading(true);
      setRetryTick((tick) => tick + 1);
    };

    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);

    return () => {
      window.removeEventListener('online', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [isAuthenticated, accountResponse, projectResponse, loading]);

  return loading;
};

export default useAccountData;
